/**
 * The app rail's data model, kept out of the component so it can be tested.
 *
 * SURF-4808 step 1b. The rail used to be `modules/sidebar/sidebarController.js`, where
 * none of this was reachable by a test: selection was decided by string surgery on
 * `$location.path()`, and groups collapsed by toggling a class on every element whose
 * `class` attribute happened to equal the group's label.
 *
 * The server sends a flat list. Dividers and the items under them are siblings, and an
 * item's group is whichever divider preceded it. That shape is the server's
 * (`server/app/configApi.js` `buildSideMenu`) and is not changed here; it is read into
 * a nested one at the boundary.
 */
import {
  Award,
  Bell,
  BookOpen,
  Boxes,
  Calendar,
  CalendarDays,
  ChartArea,
  ClipboardList,
  Contact,
  Download,
  ExternalLink,
  FileText,
  FileType,
  Files,
  Folder,
  GraduationCap,
  Images,
  Key,
  Layers,
  ListChecks,
  LogOut,
  Map,
  Megaphone,
  Menu,
  Palette,
  QrCode,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Tags,
  Ticket,
  UserCheck,
  Users,
  Workflow,
} from 'lucide-react';

/**
 * A stable key for a menu item, used to pick its icon.
 *
 * Items do not carry the name they were configured under: `buildSideMenu` spreads the
 * option and drops `mName`. A plugin item is identifiable by the plugin it routes to,
 * and a built-in by its state, href or click handler, so the key is reassembled from
 * the navigation rather than added to the server's payload.
 */
/**
 * The plugin the logo goes to when clicked.
 *
 * The navbar is the CMS's home screen in every sense that matters: it is what the app
 * opens on and the first thing in the menu. Clicking a product's logo to get back there
 * is what everyone expects, and nothing did it before.
 */
export const HOME_PLUGIN = 'menu-navbar';

/** The menu item the logo should navigate to, or undefined if this tenant has no navbar. */
export const homeItem = (menu = []) =>
  menu.find((item) => item?.navigation && menuKey(item) === HOME_PLUGIN);

export const menuKey = (item) => {
  const nav = item?.navigation || {};
  return nav.stateParams?.pluginName || nav.state || nav.href || nav.clickHandler || '';
};

/**
 * Icons, by that key. Lucide, per the 3a handoff.
 *
 * A tenant can put any plugin in its menu, including a custom one under
 * `custom-plugins/<org>/<name>`, so an unmapped key is expected rather than a mistake
 * and takes the fallback. That is also why this is a lookup and not a required field:
 * adding a plugin must never be able to break a tenant's menu.
 */
export const MENU_ICONS = {
  // Menus
  'menu-navbar': Menu,
  collections: Layers,
  'resource-categories': Tags,
  // Content library
  'pdf-manager': FileType,
  'url-manager': ExternalLink,
  'wysiwyg-manager': FileText,
  books: BookOpen,
  slideshows: Images,
  'general-files': Files,
  forms: ClipboardList,
  quizzes: ListChecks,
  surveys: ClipboardList,
  achievements: Award,
  'course-manager': GraduationCap,
  directories: Contact,
  'calendar-list': CalendarDays,
  'calendar-events': Calendar,
  workflow: Workflow,
  template: Boxes,
  // People
  'manage-app-users': Users,
  'main.manage-groups': Users,
  'manage-groups': Users,
  cohorts: Users,
  'target-audience': UserCheck,
  'main.manage-admins': ShieldCheck,
  'main.manage-subtenants': Boxes,
  // Events
  'attendance-tracking': ScanLine,
  'lead-generation': Megaphone,
  registrations: Ticket,
  // Maps, scanning, reporting
  'new-maps': Map,
  maps: Map,
  qrscanner: QrCode,
  quicksight: ChartArea,
  reporting: ChartArea,
  // Notifications and settings
  push: Bell,
  'main.push-notifications': Bell,
  'main.manage-assets': Folder,
  theme: Palette,
  selfregistration: Key,
  // Change Password opens inside the frame since SURF-4828. The href is the item as a server
  // from before that sends it, which a rolling deploy can pair with this bundle for a moment.
  'main.change-password': Key,
  '/changePassword': Key,
  '/logout': LogOut,
  previewApp: Smartphone,
  previewAppLegacy: Smartphone,
  exportContent: Download,
};

/** Every item gets an icon; an unmapped plugin gets the generic one. */
export const iconFor = (item) => MENU_ICONS[menuKey(item)] || Folder;

/**
 * The admin's own account, not a place in the CMS: Change Password (in both of the spellings
 * a server may send, see MENU_ICONS) and Log Out. They are in the account menu at the right
 * of the top bar, where every product puts them, so the rail and the breadcrumbs leave them
 * out. The server still lists them in the menu, and the account menu reads them from there.
 */
export const ACCOUNT_KEYS = new Set(['main.change-password', '/changePassword', '/logout']);

/** The menu the rail and the breadcrumbs navigate: the server's, less the account items. */
export const railMenu = (menu = []) => menu.filter((item) => !ACCOUNT_KEYS.has(menuKey(item)));

/**
 * How the Angular controller addressed a menu item, and still how selection is decided.
 *
 * Kept because the matching below compares against it as a string. It is the state name
 * with the serialised params appended, which is what made "the URL mentions both the
 * plugin and the page" a workable test.
 */
export const uiSref = (item) => {
  const nav = item?.navigation;
  if (!nav?.state) return undefined;
  return nav.stateParams ? `${nav.state}(${JSON.stringify(nav.stateParams)})` : nav.state;
};

/** Group a flat server menu into `[{ label, icon, items }]`. */
export const toGroups = (menu = []) => {
  const groups = [];
  let current = null;
  menu.forEach((item) => {
    if (item.isDivider) {
      current = { label: item.text, icon: item.icon, items: [] };
      groups.push(current);
      return;
    }
    if (!item.navigation) return;
    if (!current) {
      // A tenant whose menu opens with items rather than a divider. They belong to an
      // unlabelled group rather than being dropped, which is what a `groups[0]`
      // assumption would do.
      current = { label: '', icon: '', items: [] };
      groups.push(current);
    }
    current.items.push(item);
  });
  return groups.filter((group) => group.items.length > 0);
};

/**
 * Which item the current route belongs to.
 *
 * A faithful port of `updateSelectedItem`, which decided this by cutting up
 * `$location.path()`. The rules look arbitrary because they are: they were grown against
 * real routes. Reproducing them keeps the highlight exactly where it is today, and
 * moving them here is what finally makes them testable. In order:
 *
 *   1. `/main/plugin/<plugin>/<page>` matches the item whose `uiSref` mentions both.
 *      `newsletter` is special-cased to the first item mentioning it, because
 *      resource-categories serves two pages and the second is reached by page name only.
 *   2. `/main/` selects the item flagged `isHomePage`.
 *   3. `/main/manage-groups` matches on the plugin name alone.
 *   4. Anything else falls back to an exact state-and-params comparison.
 *   5. A plugin page none of those match (an editor, a record, any sub-page) takes its
 *      plugin's item: see `pluginItemFor`. `previous`, the item highlighted before, is what
 *      lets it keep the one the admin came from.
 *
 * Returns the matching item, or null. The caller compares by identity, as the controller
 * did, so two items with the same route still highlight one at a time.
 */
export const selectedItem = (menu = [], route = {}, previous = null) => {
  const { path = '', stateName = '', stateParams = {} } = route;
  const items = menu.filter((item) => !item.isDivider);
  const find = (predicate) => items.find((item) => predicate(item)) || null;
  const srefIncludes = (needle) => (item) => {
    const sref = uiSref(item);
    return typeof sref === 'string' && sref.includes(needle);
  };

  const afterPlugin = path.replace('/main/plugin/', '');
  const plugin = afterPlugin.split('/')[0];
  const page = path.replace(`/main/plugin/${plugin}`, '').split('/').join('');

  if (plugin === '') {
    if (page === 'main') {
      const home = items.find((item) => item.isHomePage);
      // `findIndex` returning -1 used to read `sideMenuItems[-1]`, undefined, and leave
      // nothing highlighted on a menu with no home page. Falling back to the first item
      // is the same outcome the `Number.isNaN` guard was reaching for and never got,
      // since findIndex returns -1 rather than NaN.
      return home || items[0] || null;
    }
    if (page === 'mainmanage-groups') return find(srefIncludes('manage-groups'));
  } else {
    if (page === 'newsletter') return find(srefIncludes('newsletter'));
    const match = find((item) => {
      const sref = uiSref(item);
      return typeof sref === 'string' && sref.includes(plugin) && sref.includes(page);
    });
    if (match) return match;
  }

  // A built-in screen (Manage Admins, Manage Subtenants, Change Password) is matched on its
  // state alone: the server merges `stateParams: { pageName: 'default' }` into every menu
  // item (configApi's buildSideMenu), and the route for such a screen carries no params, so
  // comparing them never matched and the rail highlighted nothing. Only a plugin state
  // needs its params to tell its pages apart.
  const exact = find(
    (item) =>
      item.navigation?.state === stateName &&
      (stateName !== 'main.plugin' ||
        JSON.stringify(item.navigation?.stateParams || {}) === JSON.stringify(stateParams || {}))
  );
  if (exact || stateName !== 'main.plugin') return exact;
  return pluginItemFor(items, stateParams, previous);
};

/** A plugin item's page name, which the server always sets (`default` when unspecified). */
const pageOf = (item) => item.navigation?.stateParams?.pageName || 'default';

/** `targets` and `logs` name their editors in the singular (`qrScannerTargetEditor`). */
const stem = (page) => page.toLowerCase().replace(/s$/, '');

/**
 * The rail item a plugin page belongs to when no item routes to that page itself. SURF-4828.
 *
 * Only a plugin's own screens are in the menu. Its editors and records
 * (`collections#collectionEditor`, `custom-plugins/iuoe/itrs#itrs-session-view-page`) are
 * not, so rules 1 to 4 find nothing for them and the rail went blank the moment an admin
 * opened one. The Angular rail hid that by never clearing its selection: a sub-page kept
 * whatever item was clicked last, and a fresh load of one highlighted nothing. QA's
 * expected result is that the current page is always highlighted, so a sub-page takes an
 * item of its own plugin, in this order:
 *
 *   1. one for the same page with other params (`menu-navbar#default` showing a menu id);
 *   2. `previous`, if it is one of this plugin's items, so a page opened from Scan Targets
 *      keeps Scan Targets rather than falling back to Scanners;
 *   3. the one whose page the sub-page names, which is all a fresh load has to go on:
 *      `newsletterForm` is the `newsletter` item's, `qrScannerTargetEditor` the `targets`
 *      item's, and `meets#meetsEditor` carries its item's page (`vod`) in its params;
 *   4. the plugin's `default` item, and failing that its first.
 *
 * A plugin with no item in this tenant's menu gets null: there is nothing honest to show.
 */
export const pluginItemFor = (items = [], stateParams = {}, previous = null) => {
  const candidates = items.filter(
    (item) =>
      item.navigation?.state === 'main.plugin' &&
      item.navigation?.stateParams?.pluginName === stateParams.pluginName
  );
  if (candidates.length === 0) return null;

  const page = stateParams.pageName || 'default';
  const samePage = candidates.find((item) => pageOf(item) === page);
  if (samePage) return samePage;
  if (previous && candidates.includes(previous)) return previous;

  const pageName = page.toLowerCase();
  const params = String(stateParams.params ?? '')
    .toLowerCase()
    .split(',');
  const named = candidates.find((item) => {
    const itemPage = pageOf(item).toLowerCase();
    return (
      itemPage !== 'default' && (pageName.includes(stem(itemPage)) || params.includes(itemPage))
    );
  });
  return named || candidates.find((item) => pageOf(item) === 'default') || candidates[0];
};

/**
 * A menu label, resolved against the tenant's message catalogue.
 *
 * `text` is a message key on some tenants and the finished label on others, and the
 * `messageKey` directive this replaces printed the key when the lookup missed. Falling
 * back to the key keeps that: a menu whose labels are unkeyed still reads correctly,
 * and a missing translation is visible rather than blank.
 */
export const resolveLabel = (messages, text, fallback) =>
  (messages && messages[text]) || fallback || text || '';

/** Subtenants by name, with Default pinned to the top. From `sortSubtenants`. */
export const sortSubtenants = (subtenants = {}) =>
  Object.values(subtenants).sort((a, b) => {
    const left = (a.name || '').toLocaleLowerCase();
    const right = (b.name || '').toLocaleLowerCase();
    if (left === 'default') return -1;
    if (right === 'default') return 1;
    return left.localeCompare(right);
  });
